import React, { useState, useRef } from 'react';
import { usePortfolioData } from '../data/portfolioData';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function CertificationsManager() {
  const { data, addCertification, updateCertification, deleteCertification } = usePortfolioData();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', provider: '', date: '', credentialUrl: '', image: '' });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const resetForm = () => setForm({ name: '', provider: '', date: '', credentialUrl: '', image: '' });

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('image', file);
    try {
      const res = await fetch(`${API_URL}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('portfolio_token')}` },
        body: fd
      });
      if (!res.ok) throw new Error('Upload failed');
      const { url } = await res.json();
      setForm(p => ({ ...p, image: url }));
    } catch (err) {
      alert('Failed to upload image');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleAdd = () => {
    if (!form.name.trim() || !form.provider.trim()) return;
    addCertification({ name: form.name, provider: form.provider, date: form.date, credentialUrl: form.credentialUrl, image: form.image });
    resetForm();
  };

  const handleEdit = (cert) => {
    setEditing(cert._id || cert.id);
    setForm({
      name: cert.name || '',
      provider: cert.provider || '',
      date: cert.date || '',
      credentialUrl: cert.credentialUrl || '',
      image: cert.image || ''
    });
  };

  const handleSave = () => {
    if (!form.name.trim() || !form.provider.trim()) return;
    updateCertification(editing, form);
    setEditing(null);
    resetForm();
  };

  const handleCancel = () => {
    setEditing(null);
    resetForm();
  };

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-blue-100 mb-4">
          {editing ? 'Edit Certification' : 'Add New Certification'}
        </h3>
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-3">
            <input
              type="text"
              placeholder="Certification name"
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              className="flex-1 min-w-[200px] px-4 py-2.5 bg-slate-800 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
            />
            <input
              type="text"
              placeholder="Provider (company name) *"
              value={form.provider}
              onChange={e => setForm(p => ({ ...p, provider: e.target.value }))}
              className="flex-1 min-w-[180px] px-4 py-2.5 bg-slate-800 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <input
              type="text"
              placeholder="Date obtained (e.g. 2024)"
              value={form.date}
              onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
              className="flex-1 min-w-[180px] px-4 py-2.5 bg-slate-800 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <input
              type="text"
              placeholder="Image URL (optional)"
              value={form.image}
              onChange={e => setForm(p => ({ ...p, image: e.target.value }))}
              className="flex-1 min-w-[200px] px-4 py-2.5 bg-slate-800 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
            />
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="px-3 py-2.5 bg-slate-700 border border-white/10 text-gray-300 rounded-xl text-sm hover:bg-slate-600 transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
          <div className="flex flex-wrap gap-3">
            <input
              type="text"
              placeholder="Credential URL (optional)"
              value={form.credentialUrl}
              onChange={e => setForm(p => ({ ...p, credentialUrl: e.target.value }))}
              className="flex-1 min-w-[200px] px-4 py-2.5 bg-slate-800 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
            />
            <div className="flex items-center gap-3">
              {editing ? (
                <>
                  <button onClick={handleSave} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-medium transition-colors">Save</button>
                  <button onClick={handleCancel} className="px-5 py-2.5 border border-white/10 text-gray-300 hover:bg-white/5 rounded-xl font-medium transition-colors">Cancel</button>
                </>
              ) : (
                <button onClick={handleAdd} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-medium transition-colors">Add</button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="space-y-3">
        {data.certifications.map(c => (
          <div key={c._id || c.id} className="flex items-start gap-4 bg-slate-900/80 border border-white/10 rounded-xl p-4 hover:border-blue-500/30">
            {c.image && (
              <img
                src={c.image}
                alt={c.name}
                className="w-20 h-20 rounded-lg object-cover shrink-0 border border-white/10"
                onError={e => { e.target.style.display = 'none' }}
              />
            )}
            <div className="flex-1 min-w-0">
              <h4 className="text-blue-300 font-semibold">{c.name}</h4>
              {c.provider && <p className="text-xs text-gray-500 mt-0.5">{c.provider}</p>}
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-gray-400">{c.date}</span>
                {c.credentialUrl && (
                  <>
                    <span className="text-xs text-gray-600">•</span>
                    <a href={c.credentialUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400/70 hover:text-blue-300 underline underline-offset-2">
                      View Credential
                    </a>
                  </>
                )}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => handleEdit(c)} className="px-3 py-1.5 text-sm border border-blue-400/30 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors">Edit</button>
              <button onClick={() => deleteCertification(c._id || c.id)} className="px-3 py-1.5 text-sm border border-red-400/30 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">Delete</button>
            </div>
          </div>
        ))}
        {data.certifications.length === 0 && (
          <p className="text-gray-500 text-center py-8">No certifications yet.</p>
        )}
      </div>
    </div>
  );
}
